import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Layout from '../components/layout/Layout';
import Button from '../components/common/Button';
import { 
  MailIcon, 
  PhoneIcon, 
  LocationMarkerIcon,
  UserIcon,
  BriefcaseIcon,
  DeviceMobileIcon,
  XCircleIcon
} from '@heroicons/react/outline';

const ContactPage: React.FC = () => {
  const [formState, setFormState] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    interest: 'market-analysis', // Default selection
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const solutionOptions = [
    { id: 'market-analysis', name: 'Market & Competitor Analysis Tool' },
    { id: 'social-media', name: 'Social Media Content Generation' },
    { id: 'crm', name: 'Customer Relationship Management' },
    { id: 'inventory', name: 'E-commerce Stock Management' },
    { id: 'chatbot', name: 'Email & WhatsApp Auto-Reply Bot' },
    { id: 'ocr', name: 'OCR Receipt Data Capture' },
    { id: 'report-generation', name: 'AI-Powered Report Generation' },
    { id: 'automation', name: 'Business Process Automation' },
    { id: 'other', name: 'Other/Not Sure Yet' },
  ];
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
    if (isSuccess) setIsSuccess(false);
    if (error) setError('');
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setIsSuccess(false);
    
    try {
      const response = await fetch('/api/contact/enquiry', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formState),
      });

      if (response.ok) {
        setIsSuccess(true);
        setFormState({
          name: '',
          company: '',
          email: '',
          phone: '',
          message: '',
          interest: 'market-analysis',
        });
      } else {
        let errorMessage = 'Failed to send message. Please try again.';
        try {
          const result = await response.json();
          errorMessage = result.message || result.error || errorMessage;
        } catch (parseError) {
          console.error("Could not parse error response:", parseError);
        }
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Submission network error:', err);
      setError('An unexpected network error occurred. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Layout>
      {/* Hero section */}
      <section className="pt-32 pb-16 bg-background dark:bg-dark-background border-b border-border/30 dark:border-dark-border/30">
        <div className="px-4 mx-auto max-w-5xl sm:px-6 lg:px-8 text-center">
          <div className="space-y-4">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-accent font-heading">Consultation</span>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground dark:text-dark-foreground">
              Get in <span className="font-serif italic text-accent font-light">Touch</span>
            </h1>
            <p className="max-w-2xl mx-auto text-sm text-foreground/60 dark:text-dark-foreground/60 font-light leading-relaxed">
              Have questions about our catalogue systems or ready to discuss custom platform engineering?
            </p>
          </div>
        </div>
      </section>
      
      {/* Contact information and form */}
      <section className="py-12 bg-background dark:bg-dark-background">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="relative rounded-2xl bg-card dark:bg-dark-card border border-border/60 dark:border-dark-border/60 overflow-hidden shadow-soft dark:shadow-dark-card">
            <div className="grid grid-cols-1 lg:grid-cols-3">
              {/* Contact information */}
              <div className="relative px-6 py-10 overflow-hidden bg-[#0A1411] text-[#E5E7EB] sm:px-10 xl:p-12">
                <div className="relative">
                  <h2 className="text-xl font-bold text-white font-heading">Contact Information</h2>
                  <p className="mt-4 text-sm text-[#E5E7EB]/70 font-light">
                    Reach out to our partners directly or submit the form to schedule a system architect review.
                  </p>
                  
                  <div className="mt-8 space-y-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <PhoneIcon className="w-6 h-6 text-accent dark:text-dark-accent" />
                      </div>
                      <div className="ml-3 text-base text-primary-foreground dark:text-dark-primary-foreground">
                        <p>+27 83 286 5179</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <MailIcon className="w-6 h-6 text-accent dark:text-dark-accent" />
                      </div>
                      <div className="ml-3 text-base text-primary-foreground dark:text-dark-primary-foreground">
                        <p>info@procreative.co.za</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-12">
                    <h3 className="text-base font-semibold text-primary-foreground/70 dark:text-dark-primary-foreground/70">Connect with us</h3>
                    <div className="flex mt-4 space-x-6">
                      <a 
                        href="https://www.facebook.com/procreativeza" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-primary-foreground/80 hover:text-accent dark:text-dark-primary-foreground/80 dark:hover:text-dark-accent"
                      >
                        <span className="sr-only">Facebook</span>
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                        </svg>
                      </a>
                      
                      <a 
                        href="https://www.linkedin.com/company/procreative/" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-primary-foreground/80 hover:text-accent dark:text-dark-primary-foreground/80 dark:hover:text-dark-accent"
                      >
                        <span className="sr-only">LinkedIn</span>
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                        </svg>
                      </a>
                      
                      <a 
                        href="https://www.instagram.com/procreative_za/" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-primary-foreground/80 hover:text-accent dark:text-dark-primary-foreground/80 dark:hover:text-dark-accent"
                      >
                        <span className="sr-only">Instagram</span>
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                           <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Contact form */}
              <div className="px-6 py-10 rounded-r-lg sm:px-10 lg:col-span-2 xl:p-12 bg-card dark:bg-dark-card">
                <h3 className="text-2xl font-bold text-primary dark:text-dark-accent">
                  Send us a message
                </h3>
                
                {isSuccess ? (
                  <motion.div 
                    className="p-6 mt-8 border rounded-lg bg-success/10 dark:bg-dark-success/20 border-success/30 dark:border-dark-success/40"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="w-6 h-6 text-success dark:text-dark-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-lg font-medium text-success dark:text-dark-success">Thank you for your message!</h3>
                        <div className="mt-2 text-sm text-success/80 dark:text-dark-success/80">
                          <p>We've received your inquiry and will get back to you shortly.</p>
                        </div>
                        <div className="mt-4">
                          <Button
                            onClick={() => setIsSuccess(false)}
                            variant="secondary"
                            size="sm"
                          >
                            Send another message
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="grid grid-cols-1 mt-8 gap-y-6 sm:grid-cols-2 sm:gap-x-8">
                    {error && (
                      <div className="p-4 border rounded-md sm:col-span-2 bg-destructive/10 dark:bg-dark-destructive/20 border-destructive/30 dark:border-dark-destructive/40">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <XCircleIcon className="w-5 h-5 text-destructive dark:text-dark-destructive-foreground" aria-hidden="true" />
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-destructive dark:text-dark-destructive-foreground">{error}</h3>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="sm:col-span-2">
                      <label htmlFor="interest" className="block text-sm font-medium text-card-foreground dark:text-dark-card-foreground">
                        I'm interested in
                      </label>
                      <div className="mt-1">
                        <select
                          id="interest"
                          name="interest"
                          value={formState.interest}
                          onChange={handleChange}
                          className="block w-full px-4 py-3 border rounded-md shadow-sm focus:outline-none focus:ring-1 sm:text-sm bg-input dark:bg-dark-input border-border dark:border-dark-border text-foreground dark:text-dark-foreground focus:ring-ring dark:focus:ring-dark-ring focus:border-ring dark:focus:border-dark-ring"
                        >
                          {solutionOptions.map(option => (
                            <option key={option.id} value={option.id} className="bg-background dark:bg-dark-input text-foreground dark:text-dark-foreground">
                              {option.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-card-foreground dark:text-dark-card-foreground">
                        Full name
                      </label>
                      <div className="relative mt-1 rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                          <UserIcon className="w-5 h-5 text-muted-foreground dark:text-dark-muted-foreground" />
                        </div>
                        <input
                          type="text"
                          name="name"
                          id="name"
                          autoComplete="name"
                          value={formState.name}
                          onChange={handleChange}
                          className={`block w-full px-4 py-3 pl-10 border rounded-md shadow-sm focus:outline-none focus:ring-1 sm:text-sm bg-input dark:bg-dark-input border-border dark:border-dark-border text-foreground dark:text-dark-foreground focus:ring-ring dark:focus:ring-dark-ring focus:border-ring dark:focus:border-dark-ring ${error ? 'border-destructive dark:border-dark-destructive' : ''}`}
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-card-foreground dark:text-dark-card-foreground">
                        Company
                      </label>
                      <div className="relative mt-1 rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                          <BriefcaseIcon className="w-5 h-5 text-muted-foreground dark:text-dark-muted-foreground" />
                        </div>
                        <input
                          type="text"
                          name="company"
                          id="company"
                          autoComplete="organization"
                          value={formState.company}
                          onChange={handleChange}
                          className="block w-full px-4 py-3 pl-10 border rounded-md shadow-sm focus:outline-none focus:ring-1 sm:text-sm bg-input dark:bg-dark-input border-border dark:border-dark-border text-foreground dark:text-dark-foreground focus:ring-ring dark:focus:ring-dark-ring focus:border-ring dark:focus:border-dark-ring"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-card-foreground dark:text-dark-card-foreground">
                        Email
                      </label>
                      <div className="relative mt-1 rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                          <MailIcon className="w-5 h-5 text-muted-foreground dark:text-dark-muted-foreground" />
                        </div>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          value={formState.email}
                          onChange={handleChange}
                          className={`block w-full px-4 py-3 pl-10 border rounded-md shadow-sm focus:outline-none focus:ring-1 sm:text-sm bg-input dark:bg-dark-input border-border dark:border-dark-border text-foreground dark:text-dark-foreground focus:ring-ring dark:focus:ring-dark-ring focus:border-ring dark:focus:border-dark-ring ${error ? 'border-destructive dark:border-dark-destructive' : ''}`}
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-card-foreground dark:text-dark-card-foreground">
                        Phone
                      </label>
                      <div className="relative mt-1 rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                          <DeviceMobileIcon className="w-5 h-5 text-muted-foreground dark:text-dark-muted-foreground" />
                        </div>
                        <input
                          type="tel"
                          name="phone"
                          id="phone"
                          autoComplete="tel"
                          value={formState.phone}
                          onChange={handleChange}
                          className="block w-full px-4 py-3 pl-10 border rounded-md shadow-sm focus:outline-none focus:ring-1 sm:text-sm bg-input dark:bg-dark-input border-border dark:border-dark-border text-foreground dark:text-dark-foreground focus:ring-ring dark:focus:ring-dark-ring focus:border-ring dark:focus:border-dark-ring"
                        />
                      </div>
                    </div>
                    
                    <div className="sm:col-span-2">
                      <label htmlFor="message" className="block text-sm font-medium text-card-foreground dark:text-dark-card-foreground">
                        Message
                      </label>
                      <div className="mt-1">
                        <textarea
                          id="message"
                          name="message"
                          rows={4}
                          value={formState.message}
                          onChange={handleChange}
                          className={`block w-full px-4 py-3 border rounded-md shadow-sm focus:outline-none focus:ring-1 sm:text-sm bg-input dark:bg-dark-input border-border dark:border-dark-border text-foreground dark:text-dark-foreground focus:ring-ring dark:focus:ring-dark-ring focus:border-ring dark:focus:border-dark-ring ${error ? 'border-destructive dark:border-dark-destructive' : ''}`}
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="sm:col-span-2">
                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        className="w-full"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Map section */}
      <section className="py-12 bg-secondary dark:bg-dark-secondary">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-bold text-primary dark:text-dark-accent">Our Location</h2>
            <p className="mt-2 text-secondary-foreground dark:text-dark-secondary-foreground">We operate primarily remotely, serving clients globally.</p>
          </div>
          
          <div className="overflow-hidden rounded-lg h-96 shadow-soft dark:shadow-dark-card">
            <div className="flex items-center justify-center h-full bg-muted dark:bg-dark-muted">
              <div className="text-center">
                <LocationMarkerIcon className="w-10 h-10 mx-auto text-accent dark:text-dark-accent" />
                <h3 className="mt-2 text-lg font-medium text-foreground dark:text-dark-foreground">ProCreative</h3>
                <p className="text-muted-foreground dark:text-dark-muted-foreground">Serving Businesses Everywhere</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
